import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, XCircle, User, Flame } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, refreshUser, isGuest, addGuestPurchase } = useAuth();
  const { t, language } = useLanguage();
  const [status, setStatus] = useState('processing');
  const [purchaseInfo, setPurchaseInfo] = useState(null);
  const hasProcessed = useRef(false);
  const [countdown, setCountdown] = useState(3);

  // Read return path from localStorage (primary) or URL params (fallback)
  const getReturnPath = () => {
    const fromStorage = localStorage.getItem('erotika_payment_return');
    const fromUrl = searchParams.get('return_to');
    return fromStorage || fromUrl || '/select-gender';
  };

  // Process payment verification - runs only once
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const sessionId = searchParams.get('session_id');
    const itemType = searchParams.get('type');
    const itemId = searchParams.get('item');
    const returnTo = getReturnPath();

    if (!sessionId) {
      localStorage.removeItem('erotika_payment_return');
      navigate(returnTo);
      return;
    }

    setPurchaseInfo({ type: itemType, id: itemId, returnTo });

    let attemptCount = 0;
    const maxAttempts = 8;

    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/purchase/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          // Flag to trigger review modal when user returns to chat
          if (localStorage.getItem('velvet_review_done') !== 'true') {
            localStorage.setItem('erotika_review_after_purchase', 'true');
          }
          if (response.data.is_guest || isGuest) {
            addGuestPurchase(itemType, itemId);
          } else {
            await refreshUser();
          }
        } else if (response.data.status === 'expired') {
          setStatus('failed');
        } else if (attemptCount < maxAttempts) {
          attemptCount++;
          setTimeout(checkStatus, 2000);
        } else {
          setStatus('failed');
        }
      } catch (error) {
        console.error('Check status error:', error);
        if (attemptCount < maxAttempts) {
          attemptCount++;
          setTimeout(checkStatus, 2000);
        } else {
          setStatus('failed');
        }
      }
    };

    checkStatus();
  }, []);

  // Auto-redirect countdown after success
  useEffect(() => {
    if (status !== 'success' || !purchaseInfo) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          localStorage.removeItem('erotika_payment_return');
          navigate(purchaseInfo.returnTo);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [status, purchaseInfo, navigate]);

  const handleContinue = () => {
    localStorage.removeItem('erotika_payment_return');
    navigate(purchaseInfo?.returnTo || '/select-gender');
  };

  const getSuccessMessage = () => {
    if (!purchaseInfo) return '';
    if (purchaseInfo.type === 'character') {
      return language === 'fr'
        ? 'Votre nouveau partenaire est maintenant disponible !'
        : 'Your new partner is now available!';
    } else if (purchaseInfo.type === 'level') {
      return language === 'fr'
        ? `Le niveau est maintenant débloqué !`
        : `The level is now unlocked!`;
    } else if (purchaseInfo.type === 'photos') {
      return language === 'fr'
        ? 'Les photos sont maintenant débloquées !'
        : 'Photos are now unlocked!';
    } else if (purchaseInfo.type === 'subscription') {
      return language === 'fr'
        ? 'Votre abonnement est maintenant actif !'
        : 'Your subscription is now active!';
    }
    return '';
  };

  const getIcon = () => {
    if (!purchaseInfo) return <CheckCircle className="text-green-400" size={40} />;
    if (purchaseInfo.type === 'character') {
      return <User className="text-purple-light" size={40} />;
    } else if (purchaseInfo.type === 'level') {
      return <Flame className="text-purple-light fill-current" size={40} />;
    }
    return <CheckCircle className="text-green-400" size={40} />;
  };

  return (
    <div className="min-h-screen bg-velvet-bg flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {status === 'processing' && (
          <div className="animate-slideUp">
            <div className="w-20 h-20 bg-purple-main/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="text-purple-light animate-spin" size={40} />
            </div>
            <h1 className="font-heading text-3xl text-velvet_text-primary mb-4">
              {t('payment_processing')}
            </h1>
            <p className="text-velvet_text-secondary">
              {language === 'fr'
                ? 'Veuillez patienter pendant la vérification...'
                : 'Please wait while we verify your payment...'
              }
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="animate-slideUp">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-green-400" size={40} />
            </div>
            <div className="w-16 h-16 bg-purple-main/20 rounded-full flex items-center justify-center mx-auto mb-4">
              {getIcon()}
            </div>
            <h1 className="font-heading text-3xl text-velvet_text-primary mb-4">
              {t('payment_success')}
            </h1>
            <p className="text-velvet_text-secondary mb-4">
              {getSuccessMessage()}
            </p>
            <p className="text-velvet_text-secondary text-sm mb-6">
              {language === 'fr'
                ? `Redirection automatique dans ${countdown}s...`
                : `Auto-redirect in ${countdown}s...`
              }
            </p>
            <Button
              onClick={handleContinue}
              className="bg-purple-main hover:bg-purple-dark text-white rounded-full px-8 py-6"
              data-testid="continue-button"
            >
              {language === 'fr' ? 'Continuer maintenant' : 'Continue now'}
            </Button>
          </div>
        )}

        {status === 'failed' && (
          <div className="animate-slideUp">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="text-red-400" size={40} />
            </div>
            <h1 className="font-heading text-3xl text-velvet_text-primary mb-4">
              {t('payment_failed')}
            </h1>
            <p className="text-velvet_text-secondary mb-8">
              {language === 'fr'
                ? 'Une erreur est survenue. Veuillez réessayer.'
                : 'An error occurred. Please try again.'
              }
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                onClick={handleContinue}
                className="bg-purple-main hover:bg-purple-dark text-white rounded-full px-8 py-6"
                data-testid="back-button"
              >
                {t('back')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
